import axios, { AxiosInstance } from 'axios';
import { getEnvVar } from '../config/env';

export type Program6Response<T> = { success: boolean; data: T };

export const PROGRAM6_DOMAINS = [
  'statistics',
  'notifications',
  'contact_activities',
  'email_campaign_events',
  'workflow_events',
  'communication_events',
  'search_updates',
  'analytics',
] as const;

export type Program6DomainKey = (typeof PROGRAM6_DOMAINS)[number];

class Program6Service {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: `${getEnvVar('VITE_FASTAPI_BASE_URL')}/api/v1/admin/program6`,
      timeout: 30000,
      headers: { 'Content-Type': 'application/json' },
    });
    this.api.interceptors.request.use((config) => {
      const token =
        localStorage.getItem('admin_auth_token') ||
        document.cookie.split('; ').find((r) => r.startsWith('auth_token='))?.split('=')[1];
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  }

  async getStatus() {
    const { data } = await this.api.get<Program6Response<Record<string, unknown>>>('/status');
    return data;
  }

  async getMigrations() {
    const { data } = await this.api.get<Program6Response<Record<string, unknown>>>('/migrations');
    return data;
  }

  async getWorkflow(domainKey?: string) {
    const { data } = await this.api.get<Program6Response<Record<string, unknown>>>('/migrations/workflow', {
      params: domainKey ? { domain_key: domainKey } : undefined,
    });
    return data;
  }

  async scheduleMigration(domainKey: string, targetPhase: string) {
    const { data } = await this.api.post<Program6Response<Record<string, unknown>>>(
      `/migrations/${encodeURIComponent(domainKey)}/schedule`,
      { target_phase: targetPhase },
    );
    return data;
  }

  async advanceMigration(domainKey: string) {
    const { data } = await this.api.post<Program6Response<Record<string, unknown>>>(
      `/migrations/${encodeURIComponent(domainKey)}/advance`,
    );
    return data;
  }

  async verifyMigration(domainKey: string) {
    const { data } = await this.api.post<Program6Response<Record<string, unknown>>>(
      `/migrations/${encodeURIComponent(domainKey)}/verify`,
    );
    return data;
  }

  async captureBaseline(domainKey: string) {
    const { data } = await this.api.post<Program6Response<Record<string, unknown>>>(
      `/migrations/${encodeURIComponent(domainKey)}/baseline`,
    );
    return data;
  }

  async rollbackMigration(domainKey: string) {
    const { data } = await this.api.post<Program6Response<Record<string, unknown>>>(
      `/migrations/${encodeURIComponent(domainKey)}/rollback`,
    );
    return data;
  }

  async comparePerformance(domainKey: string) {
    const { data } = await this.api.post<Program6Response<Record<string, unknown>>>(
      `/migrations/${encodeURIComponent(domainKey)}/performance/compare`,
    );
    return data;
  }

  async certifyDomain(domainKey: string) {
    const { data } = await this.api.post<Program6Response<Record<string, unknown>>>(
      `/migrations/${encodeURIComponent(domainKey)}/certify`,
    );
    return data;
  }

  async getPhysicalOptimizationStatus() {
    const { data } = await this.api.get<Program6Response<Record<string, unknown>>>('/physical-optimization/status');
    return data;
  }

  async runPhysicalOptimization(dryRun = true) {
    const { data } = await this.api.post<Program6Response<Record<string, unknown>>>('/physical-optimization/run', {
      dry_run: dryRun,
    });
    return data;
  }

  async getCertificationStatus() {
    const { data } = await this.api.get<Program6Response<Record<string, unknown>>>('/certification/status');
    return data;
  }

  async runFullCertification() {
    const { data } = await this.api.post<Program6Response<Record<string, unknown>>>('/certification/full');
    return data;
  }

  async runLoadCertification(concurrentRequests = 50) {
    const { data } = await this.api.post<Program6Response<Record<string, unknown>>>('/certification/load', {
      concurrent_requests: concurrentRequests,
    });
    return data;
  }

  async runResilienceCertification() {
    const { data } = await this.api.post<Program6Response<Record<string, unknown>>>('/certification/resilience');
    return data;
  }

  async runDeploymentCertification() {
    const { data } = await this.api.post<Program6Response<Record<string, unknown>>>('/certification/deployment');
    return data;
  }
}

export const program6Service = new Program6Service();
